import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { fetchLoggedInUserRoles } from '../../store/contextAPI/user/reducers/user.slice';

interface UseAuthorizationHookProps {
  requiredRoles: string[];
}

const useAuthorization = ({ requiredRoles }: UseAuthorizationHookProps) => {
  const { accounts } = useMsal();
  const dispatch = useDispatch<AppDispatch>();
  const { loggedInUserRoles, error } = useSelector(
    (state: RootState) => state.userSlice
  );

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [cachedRoles, setCachedRoles] = useState<Record<string, string[]>>({});

  const msalUser = accounts[0];
  const userId = msalUser?.localAccountId;

  useEffect(() => {
    if (msalUser && (!loggedInUserRoles || loggedInUserRoles.length === 0)) {
      setStatus('loading');
      dispatch(fetchLoggedInUserRoles());
    }
  }, [dispatch, loggedInUserRoles, msalUser]);

  const roles = useMemo(() => {
    if (!userId || !loggedInUserRoles || loggedInUserRoles.length === 0) {
      return [];
    }

    if (!cachedRoles[userId]) {
      setCachedRoles((prevCache) => ({
        ...prevCache,
        [userId]: loggedInUserRoles,
      }));
    }

    return cachedRoles[userId] || loggedInUserRoles;
  }, [cachedRoles, userId, loggedInUserRoles]);

  const isAuthorized = useMemo(() => {
    return (
      roles.length > 0 && requiredRoles.some((role) => roles.includes(role))
    );
  }, [roles, requiredRoles]);

  useEffect(() => {
    if (roles.length > 0) {
      setStatus('success');
    }
  }, [roles]);

  if (error) {
    return { isAuthorized: false, status: 'error' };
  }

  return { isAuthorized, status };
};

export default useAuthorization;
